import React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Reusable array editor for the admin Content Studio.
 * Renders a list of items with add/remove/reorder controls.
 * The consumer supplies a `renderItem(item, index, patch)` function where
 * `patch(fieldName, value)` updates a field on that item.
 */
export default function RepeaterList({ items = [], onChange, renderItem, addLabel = 'Add item', newItem = () => ({}), max, testId }) {
  const list = Array.isArray(items) ? items : [];

  const update = (idx, next) => {
    const clone = [...list];
    clone[idx] = next;
    onChange?.(clone);
  };
  const patch = (idx) => (field, value) => update(idx, { ...list[idx], [field]: value });
  const remove = (idx) => onChange?.(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const clone = [...list];
    const [x] = clone.splice(idx, 1);
    clone.splice(to, 0, x);
    onChange?.(clone);
  };
  const add = () => {
    if (max && list.length >= max) return;
    onChange?.([...list, newItem()]);
  };

  return (
    <div data-testid={testId} className="space-y-3">
      {list.map((it, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Item {i + 1}</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-md text-white/60 hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, +1)} disabled={i === list.length - 1} className="p-1.5 rounded-md text-white/60 hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-md text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          {renderItem(it, i, patch(i))}
        </div>
      ))}
      {(!max || list.length < max) && (
        <button type="button" onClick={add} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm">
          <Plus className="w-4 h-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}
